"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

const NAMESPACE = "new-spring-manufacturing";
const IMAGE_BASE = "/image/new-version/more/SpringManufacturing/SurfaceFinishes";

const FINISHES = [
	{ keyPrefix: "surfaceFinishesItem1", image: "electrogalvanizing.webp" },
	{ keyPrefix: "surfaceFinishesItem2", image: "hot-dip-galvanizing.webp" },
	{ keyPrefix: "surfaceFinishesItem3", image: "nickel-plating.webp" },
	{ keyPrefix: "surfaceFinishesItem4", image: "blackenin-blueing.webp" },
	{ keyPrefix: "surfaceFinishesItem5", image: "stainless-steel-passivation.webp" },
	{ keyPrefix: "surfaceFinishesItem6", image: "electrophoretic-coating.webp" },
	{ keyPrefix: "surfaceFinishesItem7", image: "electrostatic-spraying.webp" },
	{ keyPrefix: "surfaceFinishesItem8", image: "chrome-plating.webp" },
] as const;

const RECOMMENDATIONS = [
	"surfaceFinishesRecommendation1",
	"surfaceFinishesRecommendation2",
	"surfaceFinishesRecommendation3",
	"surfaceFinishesRecommendation4",
] as const;

export default function SpringManufacturingSurfaceFinishes() {
	const t = useTranslations(NAMESPACE);

	return (
		<section className="bg-[#ededed] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("surfaceFinishesTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("surfaceFinishesDesc")}</p>
				</header>

				<div className="grid border border-[#bfbfbf] bg-[#f3f3f3] sm:grid-cols-2 lg:grid-cols-4">
					{FINISHES.map((finish) => (
						<article
							key={finish.keyPrefix}
							className="border-b border-[#b0b0b0] p-5 last:border-b-0 lg:border-r lg:[&:nth-child(-n+4)]:border-b lg:[&:nth-child(4n)]:border-r-0 sm:[&:nth-last-child(-n+2)]:border-b-0 lg:[&:nth-last-child(-n+4)]:border-b-0"
						>
							<h3 className="mb-4 text-base font-bold text-black">{t(`${finish.keyPrefix}Title`)}</h3>
							<div className="relative mb-4 aspect-[4/3] w-full overflow-hidden bg-[#ffffff]">
								<Image
									src={`${IMAGE_BASE}/${finish.image}`}
									alt={t(`${finish.keyPrefix}ImageAlt`)}
									fill
									className="object-contain"
									sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
								/>
							</div>
							<div className="space-y-2 text-xs leading-relaxed text-black sm:text-sm">
								<p>
									<strong>{t("surfaceFinishesCoreFeaturesLabel")}</strong>{" "}
									{t(`${finish.keyPrefix}CoreFeatures`)}
								</p>
								<p>
									<strong>{t("surfaceFinishesSaltSprayLabel")}</strong> {t(`${finish.keyPrefix}SaltSpray`)}
								</p>
								<p>
									<strong>{t("surfaceFinishesSuitableMaterialsLabel")}</strong>{" "}
									{t(`${finish.keyPrefix}SuitableMaterials`)}
								</p>
								<p>
									<strong>{t("surfaceFinishesSuitableScenariosLabel")}</strong>{" "}
									{t(`${finish.keyPrefix}SuitableScenarios`)}
								</p>
							</div>
						</article>
					))}
				</div>

				<div className="mx-auto mt-8 max-w-5xl border border-gray-300 bg-white p-5 shadow-sm sm:p-6 md:p-8">
					<h3 className="mb-3 pl-[32px] text-base font-bold text-themePrimary400 sm:text-lg">
						{t("surfaceFinishesRecommendationTitle")}
					</h3>
					<p className="mb-5 pl-[32px] text-xs leading-relaxed text-black sm:text-sm">
						{t("surfaceFinishesRecommendationDesc")}
					</p>
					<div className="space-y-3">
						{RECOMMENDATIONS.map((item) => (
							<div key={item} className="flex gap-3">
								<span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-themePrimary400 text-xs font-bold text-white">
									<svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
									</svg>
								</span>
								<p className="text-xs leading-relaxed text-black sm:text-sm">
									<strong>{t(`${item}Title`)}</strong> {t(`${item}Desc`)}
								</p>
							</div>
						))}
					</div>
				</div>
			</div>
		</section>
	);
}
